import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';

const ProjectIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const LearnIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.4696 11.7893 19.9609 11.4142 19.5858C11.0391 19.2107 10.5304 19 10 19H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.4696 12.2107 19.9609 12.5858 19.5858C12.9609 19.2107 13.4696 19 14 19H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const ContactIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor"/>
    </svg>
);

const NAV_ITEMS = [
    {
        name: 'W3LO Project',
        description: 'Project Intro & Whitepaper',
        url: '/project-intro',
        Icon: ProjectIcon,
        isInternal: true
    },
    {
        name: 'Learn About',
        description: 'Training & Guides',
        url: '/learn',
        Icon: LearnIcon,
        isInternal: true
    },
    {
        name: 'Contact Us',
        description: 'Support & Inquiries',
        url: '/contact',
        Icon: ContactIcon,
        isInternal: true
    }
];

interface ExpandableSectionProps {
    title: string;
    icon: string;
    children: React.ReactNode;
    defaultExpanded?: boolean;
}

const ExpandableSection = ({ title, icon, children, defaultExpanded = false }: ExpandableSectionProps) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <div style={{
            background: 'var(--bg-card)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            overflow: 'hidden',
            marginBottom: '16px'
        }}>
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    width: '100%',
                    padding: '18px 24px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    color: 'var(--text-primary)',
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    textAlign: 'left'
                }}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '20px' }}>{icon}</span>
                    {title}
                </span>
                <span style={{
                    fontSize: '1rem',
                    transition: 'transform 0.3s ease',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: 'var(--accent-primary)'
                }}>
                    ▼
                </span>
            </button>
            <div style={{
                maxHeight: isExpanded ? 'none' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.4s ease, padding 0.3s ease',
                padding: isExpanded ? '0 24px 24px 24px' : '0 24px',
                color: 'var(--text-secondary)',
                lineHeight: 1.8,
                fontSize: '0.95rem'
            }}>
                {children}
            </div>
        </div>
    );
};

export default function ProjectIntro() {
    const navigate = useNavigate();

    return (
        <div className="landing-page" style={{ background: 'var(--bg-dark)', minHeight: '100vh' }}>
            <nav className="top-navbar">
                <div className="navbar-container">
                    <div className="navbar-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                        <BrandLogo size={36} />
                        <div className="navbar-logo-text">
                            <span>
                                web3lab
                                <span style={{ fontSize: '0.45em', opacity: 0.8, marginLeft: '2px', position: 'relative', top: '-2px' }}>.online</span>
                            </span>
                            <span className="navbar-logo-subtitle">training</span>
                        </div>
                    </div>

                    <div className="navbar-items">
                        {NAV_ITEMS.map((item) => (
                            <a
                                key={item.name}
                                href={item.url}
                                className="navbar-item"
                                onClick={(e) => {
                                    if (item.isInternal) {
                                        e.preventDefault();
                                        navigate(item.url);
                                    }
                                }}
                            >
                                <span className="navbar-item-icon">
                                    <item.Icon />
                                </span>
                                <div className="navbar-item-content">
                                    <span className="navbar-item-name">{item.name}</span>
                                    <span className="navbar-item-desc">{item.description}</span>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </nav>

            <div className="main-content" style={{
                maxWidth: '1100px',
                margin: '0 auto',
                padding: '40px 20px',
                color: 'var(--text-primary)'
            }}>
                <section style={{ marginBottom: '60px' }}>
                    <h1 style={{
                        fontSize: 'clamp(2rem, 5vw, 3rem)',
                        marginBottom: '32px',
                        color: 'var(--accent-primary)',
                        textAlign: 'center'
                    }}>
                        W3LO Project Introduction
                    </h1>

                    <div style={{
                        background: 'linear-gradient(135deg, rgba(242, 194, 48, 0.08) 0%, rgba(242, 194, 48, 0.02) 100%)',
                        padding: 'clamp(24px, 4vw, 40px)',
                        borderRadius: '16px',
                        border: '1px solid var(--accent-primary)',
                        marginBottom: '40px'
                    }}>
                        <h2 style={{
                            color: 'var(--text-primary)',
                            marginBottom: '20px',
                            fontSize: 'clamp(1.3rem, 3vw, 1.6rem)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <span>📋</span> In Short
                        </h2>
                        <p style={{ marginBottom: '20px', lineHeight: 1.8 }}>
                            W3LO is a decentralized reputation and support network for digital communities. It is designed to connect real participation, transparent support, and measurable trust inside a system that rewards genuine contribution rather than artificial visibility.
                        </p>
                        <p style={{ marginBottom: '16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                            The protocol addresses two persistent problems in digital communities:
                        </p>
                        <ul style={{ paddingLeft: '24px', marginBottom: '20px' }}>
                            <li style={{ marginBottom: '12px' }}><strong>Trust without reliability</strong> — where follower counts, engagement metrics, and social visibility can be inflated or manipulated.</li>
                            <li style={{ marginBottom: '12px' }}><strong>Discovery without support</strong> — where promising contributors are often recognized too late, or not supported early enough.</li>
                        </ul>
                        <p style={{ margin: 0 }}>
                            W3LO creates a structured environment where reputation is earned through participation, support is coordinated through transparent rules, and utility is tied to real protocol usage.
                        </p>
                    </div>

                    <ExpandableSection title="What W3LO Is" icon="🔮" defaultExpanded={true}>
                        <p style={{ marginBottom: '16px' }}>
                            W3LO is not just a social platform, and it is not just a token system. It is a coordination layer for reputation, support, and access.
                        </p>
                        <p style={{ marginBottom: '16px' }}>
                            At its core, W3LO helps digital communities do three things:
                        </p>
                        <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
                            <li style={{ marginBottom: '10px' }}>Identify real participation</li>
                            <li style={{ marginBottom: '10px' }}>Surface meaningful contributors</li>
                            <li style={{ marginBottom: '10px' }}>Connect support to transparent network signals</li>
                        </ul>
                        <p style={{ margin: 0 }}>
                            This makes W3LO useful for communities that want better discovery, stronger trust, and a more accountable way to coordinate incentives.
                        </p>
                    </ExpandableSection>

                    <ExpandableSection title="Why It Exists" icon="❓">
                        <p style={{ marginBottom: '16px' }}>
                            Most online systems still measure presence in shallow ways. Follower count, post volume, and engagement numbers can be bought, automated, or inflated. As a result, the systems that are supposed to reward value often reward visibility instead.
                        </p>
                        <p style={{ marginBottom: '16px' }}>
                            <strong style={{ color: 'var(--accent-primary)' }}>W3LO exists to solve that gap.</strong>
                        </p>
                        <p style={{ marginBottom: '12px' }}>
                            Instead of asking how large a profile looks, W3LO asks whether the behavior behind it is real, consistent, and valuable over time. Instead of relying on passive metrics, it creates a reputation framework built on participation, support, and verifiable interaction.
                        </p>
                    </ExpandableSection>

                    <ExpandableSection title="How It Works" icon="⚙️">
                        <p style={{ marginBottom: '20px' }}>
                            W3LO brings together several layers that reinforce one another:
                        </p>
                        <div style={{ display: 'grid', gap: '16px', marginBottom: '20px' }}>
                            <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px', fontSize: '1rem' }}>🧠 Reputation Layer</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>The reputation layer measures genuine participation and behavioral consistency. It helps the protocol distinguish authentic users from low-quality or automated activity.</p>
                            </div>
                            <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px', fontSize: '1rem' }}>🤝 Support Layer</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>The support layer allows communities and backers to direct attention and resources toward contributors who show early promise or sustained value.</p>
                            </div>
                            <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px', fontSize: '1rem' }}>🎁 Token Utility Layer</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>The token utility layer coordinates access, program participation, and protocol activity. It is used to unlock features, enter missions and events, and support network-level operations.</p>
                            </div>
                            <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px', fontSize: '1rem' }}>✓ Validation Layer</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>The validation layer helps maintain integrity across the network. It supports rule enforcement, reward coordination, and trust-based review where needed.</p>
                            </div>
                        </div>
                        <p style={{ margin: 0, fontWeight: 500, color: 'var(--text-primary)' }}>
                            Together, these layers create a system where reputation is not just measured — it is operationalized.
                        </p>
                    </ExpandableSection>

                    <ExpandableSection title="Core Principles" icon="🏛️">
                        <p style={{ marginBottom: '16px' }}>
                            W3LO is built around five principles:
                        </p>
                        <div style={{ display: 'grid', gap: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <span style={{ color: 'var(--accent-primary)', fontWeight: 700, minWidth: '24px' }}>1.</span>
                                <div><strong style={{ color: 'var(--text-primary)' }}>Participation over appearance</strong><br/><span style={{ fontSize: '0.9rem' }}>Real behavior matters more than inflated numbers.</span></div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <span style={{ color: 'var(--accent-primary)', fontWeight: 700, minWidth: '24px' }}>2.</span>
                                <div><strong style={{ color: 'var(--text-primary)' }}>Trust through verification</strong><br/><span style={{ fontSize: '0.9rem' }}>Signals should be measurable, explainable, and resistant to manipulation.</span></div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <span style={{ color: 'var(--accent-primary)', fontWeight: 700, minWidth: '24px' }}>3.</span>
                                <div><strong style={{ color: 'var(--text-primary)' }}>Support with accountability</strong><br/><span style={{ fontSize: '0.9rem' }}>Backing and discovery should be transparent, not arbitrary.</span></div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <span style={{ color: 'var(--accent-primary)', fontWeight: 700, minWidth: '24px' }}>4.</span>
                                <div><strong style={{ color: 'var(--text-primary)' }}>Utility over speculation</strong><br/><span style={{ fontSize: '0.9rem' }}>The token exists to support protocol usage, not to function as a financial promise.</span></div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <span style={{ color: 'var(--accent-primary)', fontWeight: 700, minWidth: '24px' }}>5.</span>
                                <div><strong style={{ color: 'var(--text-primary)' }}>Privacy with control</strong><br/><span style={{ fontSize: '0.9rem' }}>Users should be able to participate without unnecessary exposure or loss of control.</span></div>
                            </div>
                        </div>
                    </ExpandableSection>

                    <ExpandableSection title="Features" icon="✨">
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                        <th style={{ textAlign: 'left', padding: '12px 16px', color: 'var(--accent-primary)' }}>Feature</th>
                                        <th style={{ textAlign: 'left', padding: '12px 16px', color: 'var(--accent-primary)' }}>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>🧠 <strong>Reputation Layer</strong></td>
                                        <td style={{ padding: '14px 16px' }}>Builds credibility from participation, behavioral consistency, and community interaction rather than follower count alone.</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>🗳️ <strong>Voting Layer</strong></td>
                                        <td style={{ padding: '14px 16px' }}>Helps shape visibility and surface contributors who show genuine value over time.</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>🤝 <strong>Support Layer</strong></td>
                                        <td style={{ padding: '14px 16px' }}>Allows backers to support promising contributors early through transparent, rules-based flows.</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>🎁 <strong>Token Utility</strong></td>
                                        <td style={{ padding: '14px 16px' }}>Coordinates access, incentives, and protocol participation across the system.</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>🔒 <strong>Privacy</strong></td>
                                        <td style={{ padding: '14px 16px' }}>Uses selective transparency and revocable permissions to protect user control.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </ExpandableSection>

                    <ExpandableSection title="Roles in the Network" icon="👥">
                        <div style={{ display: 'grid', gap: '16px', marginBottom: '20px' }}>
                            <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px', fontSize: '1rem' }}>💰 Capital Providers</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>Capital providers supply the economic base of the network by funding campaigns, reward pools, and participation programs.</p>
                            </div>
                            <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px', fontSize: '1rem' }}>⚡ Contributors</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>Contributors complete verified tasks and actions that build reputation and create meaningful network activity.</p>
                            </div>
                            <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px', fontSize: '1rem' }}>✓ Validators</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>Validators help maintain trust by reviewing, confirming, and supporting important network actions.</p>
                            </div>
                        </div>
                    </ExpandableSection>

                    <ExpandableSection title="Fee Model" icon="💳">
                        <p style={{ marginBottom: '16px' }}>
                            W3LO uses a transparent service-fee model. Fees are charged only when the platform provides a real operational function.
                        </p>
                        <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
                            <div style={{ background: 'var(--bg-dark)', padding: '16px', borderRadius: '8px', borderLeft: '3px solid var(--accent-primary)' }}>
                                <strong style={{ color: 'var(--text-primary)' }}>Setup Fee:</strong>
                                <span style={{ fontSize: '0.9rem' }}> charged when a new campaign or reward program is created.</span>
                            </div>
                            <div style={{ background: 'var(--bg-dark)', padding: '16px', borderRadius: '8px', borderLeft: '3px solid var(--accent-primary)' }}>
                                <strong style={{ color: 'var(--text-primary)' }}>Reward Coordination Fee:</strong>
                                <span style={{ fontSize: '0.9rem' }}> charged when rewards are distributed or settled.</span>
                            </div>
                            <div style={{ background: 'var(--bg-dark)', padding: '16px', borderRadius: '8px', borderLeft: '3px solid var(--accent-primary)' }}>
                                <strong style={{ color: 'var(--text-primary)' }}>Validation Fee:</strong>
                                <span style={{ fontSize: '0.9rem' }}> charged when a validator-reviewed action requires trust, review, or oversight.</span>
                            </div>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.9rem' }}>
                            A defined share of collected service fees is allocated to the validator reward pool, while the remaining share supports protocol operations, infrastructure, and long-term maintenance.
                        </p>
                    </ExpandableSection>

                    <ExpandableSection title="Now and Next" icon="🚀">
                        <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                            <div style={{ background: 'var(--bg-dark)', padding: '24px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                                <h4 style={{ color: '#22c55e', marginBottom: '16px', fontSize: '1.1rem' }}>Now</h4>
                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem', lineHeight: 1.8 }}>
                                    <li style={{ marginBottom: '8px' }}>Profiles and nodes</li>
                                    <li style={{ marginBottom: '8px' }}>Reputation mechanics</li>
                                    <li style={{ marginBottom: '8px' }}>Voting and support flows</li>
                                    <li>Token utility foundations</li>
                                </ul>
                            </div>
                            <div style={{ background: 'var(--bg-dark)', padding: '24px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <h4 style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '1.1rem' }}>Next</h4>
                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem', lineHeight: 1.8 }}>
                                    <li style={{ marginBottom: '8px' }}>Backer tools</li>
                                    <li style={{ marginBottom: '8px' }}>Dashboard analytics</li>
                                    <li style={{ marginBottom: '8px' }}>Governance experiments</li>
                                    <li style={{ marginBottom: '8px' }}>Reputation portability</li>
                                    <li>Ecosystem integrations</li>
                                </ul>
                            </div>
                        </div>
                    </ExpandableSection>

                    <ExpandableSection title="Roadmap 2026" icon="🗺️">
                        <div style={{ display: 'grid', gap: '20px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                                <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                    <h4 style={{ color: 'var(--accent-primary)', marginBottom: '12px', fontSize: '1rem' }}>Q1 2026</h4>
                                    <p style={{ fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)', fontSize: '0.95rem' }}>Network Foundation</p>
                                    <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.85rem', lineHeight: 1.7 }}>
                                        <li style={{ marginBottom: '6px' }}>Profiles and nodes</li>
                                        <li style={{ marginBottom: '6px' }}>Voting mechanics</li>
                                        <li>Reputation Score v1</li>
                                    </ul>
                                </div>
                                <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                    <h4 style={{ color: 'var(--accent-primary)', marginBottom: '12px', fontSize: '1rem' }}>Q2 2026</h4>
                                    <p style={{ fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)', fontSize: '0.95rem' }}>Participation Growth</p>
                                    <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.85rem', lineHeight: 1.7 }}>
                                        <li style={{ marginBottom: '6px' }}>Token utilities</li>
                                        <li style={{ marginBottom: '6px' }}>Discovery tools</li>
                                        <li>Dashboard analytics</li>
                                    </ul>
                                </div>
                                <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                    <h4 style={{ color: 'var(--accent-primary)', marginBottom: '12px', fontSize: '1rem' }}>Q3 2026</h4>
                                    <p style={{ fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)', fontSize: '0.95rem' }}>Support Coordination</p>
                                    <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.85rem', lineHeight: 1.7 }}>
                                        <li style={{ marginBottom: '6px' }}>Support flows</li>
                                        <li style={{ marginBottom: '6px' }}>Incentive tracking</li>
                                        <li>Ecosystem expansion</li>
                                    </ul>
                                </div>
                                <div style={{ background: 'var(--bg-dark)', padding: '20px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                                    <h4 style={{ color: 'var(--accent-primary)', marginBottom: '12px', fontSize: '1rem' }}>Q4 2026</h4>
                                    <p style={{ fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)', fontSize: '0.95rem' }}>Ecosystem Expansion</p>
                                    <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.85rem', lineHeight: 1.7 }}>
                                        <li style={{ marginBottom: '6px' }}>Social integrations</li>
                                        <li style={{ marginBottom: '6px' }}>Reputation portability</li>
                                        <li>Governance experiments</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </ExpandableSection>

                    <div style={{
                        background: 'rgba(242, 194, 48, 0.05)',
                        padding: '24px',
                        borderRadius: '12px',
                        borderLeft: '4px solid var(--accent-primary)',
                        marginTop: '32px'
                    }}>
                        <h3 style={{ color: 'var(--accent-primary)', marginBottom: '16px', fontSize: '1.1rem', marginTop: 0 }}>
                            ⚖️ Legal Notice
                        </h3>
                        <p style={{ marginBottom: '12px', opacity: 0.9, fontSize: '0.9rem' }}>
                            This document explains W3LO's product vision and user-facing design. It does not constitute financial, legal, or tax advice.
                        </p>
                        <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>
                            Participation does not guarantee profit, value appreciation, or any financial outcome. Users are responsible for their own decisions and for compliance with the laws of their jurisdiction.
                        </p>
                    </div>

                    <div style={{
                        marginTop: '40px',
                        background: 'var(--bg-card)',
                        padding: '32px',
                        borderRadius: '16px',
                        border: '1px solid var(--border-color)',
                        textAlign: 'center'
                    }}>
                        <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1.3rem' }}>
                            Short Version
                        </h3>
                        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, maxWidth: '700px', margin: '0 auto', fontSize: '1rem' }}>
                            W3LO is a decentralized reputation and support network for digital communities. It combines verified participation, transparent support flows, and utility-based protocol access to solve two major problems: trust without reliability and discovery without support.
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
}